/**
 * ModelPickDiffView — model-picker-style list for a `default_model`
 * proposal (`propose_edit_agent(field='default_model')`).
 *
 * The AoA proposes a single model id in `new_value` (with the agent's
 * current model — possibly null — in `old_value`). The plain text diff
 * rendered a bare id like `claude-haiku-4-5-20251001`, which reads as
 * noise to a non-technical reviewer. This view instead shows the
 * workspace's model registry as a vertical list styled like the chat's
 * model picker rows: provider glyph + friendly display name, with the
 * PROPOSED model highlighted as the selected row and the CURRENT model
 * dimmed with a small tag. Edit is suppressed for these cards (see
 * ProposalCard.isModelProposal) — the user must Accept or Reject the
 * exact id — so the labels here must be unambiguous.
 */

import React, { useEffect, useMemo, useState } from 'react';
import api from '@/services/api';
import { ProviderIcon } from '@/components/ProviderIcon';
import { findModelEntry } from '@modules/conversations/components/ModelSelector';
import { useLanguage } from '@hooks/useLanguage';
import './ModelPickDiffView.css';

interface ModelInfo {
  id: string;
  name: string;
  provider: string;
  /** Concrete release this family currently runs for the tenant. */
  resolved_model?: string;
  /** Every release in this family — lets a legacy ref still match. */
  version_ids?: string[];
  resolved_model_name?: string;
  /** Switched-off family keys this family runs ("gpt" on GPT Terra). */
  aliases?: string[];
  enabled?: boolean;
}

interface Props {
  /** Current default model id (from proposal.old_value; may be null). */
  before: string | null;
  /** Proposed model id (from proposal.new_value). */
  after: string;
}

/** Infer a ProviderIcon provider key from a bare model id. Used when the
 *  registry hasn't loaded yet (or the proposed id isn't in it) so the row
 *  still carries the right glyph. */
function providerOfModelId(id: string): string {
  const s = (id || '').toLowerCase();
  if (s.startsWith('claude') || s.includes('anthropic')) return 'anthropic';
  if (s.startsWith('gpt') || /^o\d/.test(s) || s.includes('openai')) return 'openai';
  if (s.includes('gemini') || s.includes('google')) return 'google';
  if (s.includes('kimi') || s.includes('moonshot')) return 'moonshot';
  return '';
}

export const ModelPickDiffView: React.FC<Props> = ({ before, after }) => {
  const { t } = useLanguage();
  const [models, setModels] = useState<ModelInfo[]>([]);

  // Pull the workspace's model registry once — the same endpoint the chat's
  // ConversationPanel / AllowedModelsDiffView use. Already tenant-filtered
  // server-side. While loading (or on failure) the list below still renders
  // the proposed / current rows synthesized from their bare ids.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await api.get('/ai/models/');
        const data = response.data;
        const list: ModelInfo[] = (data.models || data || []).map((m: any) => ({
          id: m.id,
          name: m.name || m.id,
          provider: m.provider || '',
          resolved_model: m.resolved_model || undefined,
          version_ids: m.version_ids || undefined,
          resolved_model_name: m.resolved_model_name || undefined,
          aliases: m.aliases || undefined,
          enabled: m.enabled,
        }));
        if (!cancelled) setModels(list);
      } catch {
        // Non-fatal: the synthesized rows below still show the exact ids
        // the user is being asked to Accept / Reject.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // The proposal carries a model REF, which may be a family key ('haiku') or
  // a concrete release ('claude-haiku-4-5-20251001') — the AoA validator
  // accepts both. The list holds FAMILIES, so match the same way every other
  // picker does (family key OR the tenant's pinned release) and highlight the
  // family row. Without this, a concrete ref matched nothing and got prepended
  // as a raw-id row sitting next to its own family: a duplicate entry showing
  // exactly the kind of raw id these cards exist to hide.
  const proposedEntry = useMemo(() => findModelEntry(models, after), [models, after]);
  const currentEntry = useMemo(() => findModelEntry(models, before), [models, before]);
  const proposedKey = proposedEntry?.id ?? after;
  const currentKey = currentEntry?.id ?? before;

  const rows = useMemo<ModelInfo[]>(() => {
    const list = [...models];
    const known = new Set(list.map((m) => m.id));
    // A ref that matches no family at all (a retired id, or a fine-tuned
    // model) still renders, so the "current → proposed" story stays complete.
    if (currentKey && currentKey !== proposedKey && !known.has(currentKey)) {
      list.unshift({ id: currentKey, name: currentKey, provider: providerOfModelId(currentKey) });
    }
    if (proposedKey && !known.has(proposedKey)) {
      list.unshift({ id: proposedKey, name: proposedKey, provider: providerOfModelId(proposedKey) });
    }
    return list;
  }, [models, currentKey, proposedKey]);

  return (
    <div className="aoa-model-pick" data-testid="aoa-model-pick">
      <div className="aoa-model-pick__heading">
        {t('metaAgent.proposal.modelPick.heading')}
      </div>
      <div className="aoa-model-pick__list" role="listbox" aria-readonly>
        {rows.map((m) => {
          const isProposed = m.id === proposedKey;
          const isCurrent = !isProposed && currentKey != null && m.id === currentKey;
          return (
            <div
              key={m.id}
              role="option"
              aria-selected={isProposed}
              className={`aoa-model-pick__row${isProposed ? ' aoa-model-pick__row--proposed' : ''}${isCurrent ? ' aoa-model-pick__row--current' : ''}`}
              title={m.resolved_model_name ? `${m.name} — ${m.resolved_model_name}` : m.id}
              data-testid={isProposed ? 'aoa-model-pick-proposed' : undefined}
            >
              <ProviderIcon provider={m.provider || providerOfModelId(m.id)} size={14} />
              {/* The pinned RELEASE is the label, matching the chat picker and
                  the agent editor. Family names stay admin-side. */}
              <span className="aoa-model-pick__name" dir="auto">
                {m.resolved_model_name || m.name}
              </span>
              {isProposed && (
                <span className="aoa-model-pick__tag aoa-model-pick__tag--proposed">
                  {t('metaAgent.proposal.modelPick.proposedTag')}
                </span>
              )}
              {isCurrent && (
                <span className="aoa-model-pick__tag aoa-model-pick__tag--current">
                  {t('metaAgent.proposal.modelPick.currentTag')}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
